// ─────────────────────────────────────────────────────────────────────────────
// trainingApp.ts
//
// Orchestrates the Training phone experience. Mirrors `aflApp.ts` but
// uses only Training-specific event types, hero, and tabs. The two apps
// share nothing beyond the phone shell chrome and the live-state store.
// ─────────────────────────────────────────────────────────────────────────────

import { h, mount } from "../../ui/dom.js";
import {
	renderPhoneShell,
	type ConnectionStatus,
	type TabDef,
} from "../../ui/phoneShell.js";
import { liveStateStore } from "../../state/liveStateStore.js";
import type { LiveState } from "../../contract/liveState.js";
import type { GameSession } from "../../lib/session.js";
import { safeMinSec, safeText } from "../../lib/safe.js";
import { trainingHero } from "./trainingHero.js";
import { controlTab } from "./screens/controlTab.js";
import { builderTab } from "./screens/builderTab.js";
import { optionsTab } from "./screens/optionsTab.js";
import { moreTab } from "./screens/moreTab.js";

type TrainingTabKey = "control" | "builder" | "options" | "more";

const TABS: TabDef<TrainingTabKey>[] = [
	{ key: "control", label: "Control", icon: "⏱" },
	{ key: "builder", label: "Add timer", icon: "➕" },
	{ key: "options", label: "Options", icon: "⚙" },
	{ key: "more", label: "More", icon: "•••" },
];

export function runTrainingApp(
	host: HTMLElement,
	session: GameSession,
	onLeave: () => void
): void {
	let activeTab: TrainingTabKey = "control";
	let connectionStatus: ConnectionStatus = "connecting";
	let cleanedUp = false;

	const leave = (): void => {
		if (cleanedUp) return;
		cleanedUp = true;
		unsubscribeState();
		unsubscribeConn();
		void liveStateStore.stop();
		onLeave();
	};

	const render = (state: LiveState | null): void => {
		const subtitle = state
			? `${safeText(state.training.activeTitle, "Idle")} · ${safeMinSec(state.training.activeRemainingSeconds)}`
			: "Waiting for desktop…";

		renderPhoneShell<TrainingTabKey>(host, {
			sport: "training",
			title: "Training",
			subtitle,
			tabs: TABS,
			activeTab,
			onTabChange: (k) => {
				activeTab = k;
				render(liveStateStore.state);
			},
			connectionStatus,
			hero: trainingHero(state),
			body: state ? bodyFor(activeTab, session, state, leave) : waitingCard(),
		});
	};

	const unsubscribeState = liveStateStore.subscribe(render);
	const unsubscribeConn = liveStateStore.onConnection((status) => {
		connectionStatus = status;
		render(liveStateStore.state);
	});

	void liveStateStore.start(session.id).catch((err: unknown) => {
		const msg = err instanceof Error ? err.message : String(err);
		mount(
			host,
			h(
				"div",
				{ class: "phone-shell theme-training p-6 text-sm text-accent-red" },
				`Couldn't load training session: ${msg}`
			)
		);
	});
}

function bodyFor(
	tab: TrainingTabKey,
	session: GameSession,
	state: LiveState,
	onLeave: () => void
): HTMLElement {
	switch (tab) {
		case "control":
			return controlTab(session.id, state);
		case "builder":
			return builderTab(session.id);
		case "options":
			return optionsTab(session.id, state);
		case "more":
			return moreTab(state, {
				sessionId: session.id,
				joinCode: session.joinCode,
				onLeave,
			});
	}
}

function waitingCard(): HTMLElement {
	return h(
		"div",
		{ class: "card text-center text-sm text-slate-400" },
		"Waiting for the desktop to send the first training snapshot…"
	);
}
